import { computed, signal, Signal, WritableSignal } from "@angular/core";
import { PaginationInfo } from "../../interfaces/cache-state.interface";
import { TABLE_PAGE_SIZE } from "../../constants/pagination.constants";

/**
 * Base class for cached services with standardized pagination and state management
 */
export abstract class CachedServiceBase<T> {
  /** Internal data signal */
  protected readonly _data: WritableSignal<T[]> = signal([]);

  /** Current page index signal (0-based) */
  protected readonly _currentPageIndex: WritableSignal<number> = signal(0);

  // =============================================================================
  // PUBLIC SIGNALS
  // =============================================================================

  /** Computed data for external access */
  public readonly data: Signal<T[]> = computed(() => this._data());

  /** Abstract method to apply client-side filters */
  protected abstract getFilteredData(): Signal<T[]>;

  /** Computed current page data */
  public readonly currentPageData: Signal<T[]> = computed(() => {
    const data = this.getFilteredData()();
    const currentPage = this._currentPageIndex();
    const startIndex = currentPage * TABLE_PAGE_SIZE;
    const endIndex = startIndex + TABLE_PAGE_SIZE;
    return data.slice(startIndex, endIndex);
  });

  /** Computed pagination information */
  public readonly paginationInfo: Signal<PaginationInfo> = computed(() => {
    const data = this.getFilteredData()();
    const currentPage = this._currentPageIndex();
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / TABLE_PAGE_SIZE);

    return {
      currentPage,
      totalPages,
      hasNextPage: currentPage < totalPages - 1,
      hasPreviousPage: currentPage > 0,
      shouldShowPagination: totalPages > 1,
      pageSize: TABLE_PAGE_SIZE,
      totalItems,
    };
  });

  // =============================================================================
  // PAGINATION METHODS
  // =============================================================================

  /**
   * Navigate to the next page
   */
  public goToNextPage(): void {
    const pagination = this.paginationInfo();
    if (pagination.hasNextPage) {
      this._currentPageIndex.set(pagination.currentPage + 1);
    }
  }

  /**
   * Navigate to the previous page
   */
  public goToPreviousPage(): void {
    const pagination = this.paginationInfo();
    if (pagination.hasPreviousPage) {
      this._currentPageIndex.set(pagination.currentPage - 1);
    }
  }

  /**
   * Go to a specific page
   */
  public goToPage(pageIndex: number): void {
    const pagination = this.paginationInfo();
    if (pageIndex >= 0 && pageIndex < pagination.totalPages) {
      this._currentPageIndex.set(pageIndex);
    }
  }

  /**
   * Reset pagination to first page
   */
  public resetPagination(): void {
    this._currentPageIndex.set(0);
  }

  // =============================================================================
  // DATA MANAGEMENT METHODS
  // =============================================================================

  /**
   * Update the cached data and reset pagination
   */
  protected updateData(data: T[]): void {
    this._data.set(data);
    this.resetPagination();
  }

  /**
   * Clear all cached data and reset pagination
   */
  protected clearData(): void {
    this._data.set([]);
    this.resetPagination();
  }

  /**
   * Add items to the cached data
   */
  protected addData(items: T[]): void {
    this._data.update((currentData) => [...currentData, ...items]);
  }

  /**
   * Remove item from cached data by predicate
   */
  protected removeData(predicate: (item: T) => boolean): void {
    this._data.update((currentData) => currentData.filter((item) => !predicate(item)));
  }

  /**
   * Update item in cached data by predicate
   */
  protected updateItem(
    predicate: (item: T) => boolean,
    updater: (item: T) => T,
  ): void {
    this._data.update((currentData) => 
      currentData.map((item) => (predicate(item) ? updater(item) : item))
    );
  }

  // =============================================================================
  // ABSTRACT METHODS
  // =============================================================================

  /**
   * Abstract method to refresh data from backend
   * Must be implemented by each cached service
   */
  public abstract refresh(): void;

  /**
   * Abstract method to reset service state
   * Must be implemented by each cached service
   */
  public abstract reset(): void;
}
